// Email verification sagas
import { put } from 'redux-saga/effects';
import type { Action } from 'redux-actions';

// Api
import client from '@api';

// Services
import { setActiveSessionToken } from '@services/sessionToken';

// Actions
import { ShowToast } from '@actions/toast';
import { SelectOrganization } from '@actions/organization';
import {
  PassUserOnboarding,
  UserDataLoaded,
  UserEmailTempVerification,
  UserEmailVerification,
} from '@actions/user';
import { UpdateAccount } from '@actions/account';
import { SetRedirectControl } from '@actions/redirectControl';
import { SetLoading } from '@actions/loading';

import {
  verifyEmail,
  verifyTempAccountCreadentialsUpdate,
} from '../../graphql/mutations/account';

import type {
  Account,
  EmailVerificationPayload,
  TempEmailVerificationPayload,
  VerifyEmailResponse,
  VerifyTempResponse,
} from './types';

export function* verifyUserEmail({
  payload: { secret },
}: Action<EmailVerificationPayload>): Generator<unknown, void, unknown> {
  try {
    yield put(SetLoading(true));
    const response = (yield client().mutate({
      mutation: verifyEmail,
      variables: { input: { secret } },
    })) as VerifyEmailResponse;

    const result = response.data.verifyEmail;
    const { _id, email, phoneNumber } = result?.account;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      userID: _id,
      event: 'emailVerification',
      email,
      phoneNumber,
    });

    yield put(UpdateAccount(result.account));
    yield put(SelectOrganization(result.account?.organizations?.[0]));

    // Active session JWT: org JWT if the user has at least one org,
    // otherwise the account JWT (mid-onboarding case).
    const firstOrgToken = result.account?.organizations?.[0]?.token;
    if (firstOrgToken) {
      setActiveSessionToken(firstOrgToken);
    } else if (result?.tokens?.access) {
      setActiveSessionToken(result.tokens.access);
    }

    yield put(SetRedirectControl('/onboarding/persona'));
  } catch (error) {
    const err = error as Error;
    if (err.message.includes('Account already verified')) {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'Email has already passed verification',
        }),
      );
      // yield put(SetRedirectControl('/auth/login'));
    }
    if (err.message === 'Wrong challenge') {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'The verification code is incorrect, please try again',
        }),
      );
    }
    if (err.message === 'Challenge expired') {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'The verification code has expired, please try again',
        }),
      );
    }
  } finally {
    yield put(SetLoading(false));
  }
}

export function* verifyTempUserEmail({
  payload: { challenge, isProfile = false },
}: Action<TempEmailVerificationPayload>): Generator<unknown, void, unknown> {
  try {
    yield put(SetLoading(true));
    const response = (yield client().mutate({
      mutation: verifyTempAccountCreadentialsUpdate,
      variables: { challenge },
    })) as VerifyTempResponse;

    const result = response.data.verifyTempAccountCreadentialsUpdate;
    if (result?.tokens?.access) {
      setActiveSessionToken(result.tokens.access);
    }

    yield put(
      UserDataLoaded({
        users: {
          [result.account?.username || '']: result.account,
        },
        me: result.account,
      }),
    );

    const ab = (
      meta: Account['meta'],
    ): Record<string, unknown> | undefined | false =>
      meta &&
      meta?.ab_flags?.length &&
      meta?.ab_flags.reduce(
        (acc: Record<string, unknown>, { key, value }) => ({
          ...acc,
          [`ab_${key}`]: value,
        }),
        {},
      );

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      userID: result?.account?._id,
      ...ab(result?.account?.meta),
      email: result?.account?.email,
      custom_sourceMarbleId: result?.account?.marbleId,
    });

    if (isProfile) {
      localStorage.setItem('onboardingPath', '/onboarding/profile');
      yield put(SetRedirectControl('/onboarding/profile'));
    } else {
      yield put(PassUserOnboarding(true));
      localStorage.removeItem('onboardingPath');
      localStorage.removeItem('fbqTrack');
      yield put(SetRedirectControl('/'));
    }
  } catch (error) {
    const err = error as Error;
    if (err.message.includes('Account already verified')) {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'Email has already passed verification',
        }),
      );
      // yield put(SetRedirectControl('/auth/login'));
    }
    if (err.message === 'Wrong challenge') {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'The verification code is incorrect, please try again',
        }),
      );
    }
    if (err.message === 'Challenge expired') {
      yield put(
        ShowToast({
          title: 'Verification Error',
          message: 'The verification code has expired, please try again',
        }),
      );
    }
  } finally {
    yield put(SetLoading(false));
  }
}

// Action creators for watchers
export const verificationActions = {
  UserEmailVerification,
  UserEmailTempVerification,
};
